import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Icon } from "@juiceforward/narrative-player";
import type { PipelineManifest, PipelineManifestInfo } from "../narratives";
import { manifestInfoKey, manifestInfoLabel } from "../narratives";
import type { WorkspaceOption } from "../utils/fetchShareToken";
import type { DeviceType } from "@juiceforward/narrative-player";

const HEADER_SELECT_CLASS =
  "w-full appearance-none bg-gray-800/80 border border-gray-700 rounded-md pl-2 pr-6 py-1 text-xs text-gray-200 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 cursor-pointer truncate transition-colors";
const HEADER_LABEL_CLASS =
  "w-16 flex-shrink-0 text-[10px] uppercase tracking-wider text-gray-500";
const HEADER_CHEVRON_CLASS =
  "absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none";

const STORAGE_KEY = "dev_navigator_width";
const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 250;
const MAX_WIDTH_PERCENT = 50;

const deviceConfig: Record<DeviceType, { icon: string; label: string }> = {
  desktop: { icon: "display", label: "Desktop" },
  tablet: { icon: "rectangle-wide", label: "Tablet" },
  phone: { icon: "mobile", label: "Phone" },
};

const deviceTypes: DeviceType[] = ["desktop", "tablet", "phone"];

interface DevNavigatorProps {
  currentReportId: string;
  manifest: PipelineManifest;
  allManifests: PipelineManifestInfo[];
  onNavigate: (reportId: string) => void;
  onManifestChange: (info: PipelineManifestInfo) => void;
  onClose: () => void;
  deviceType: DeviceType;
  onDeviceTypeChange: (type: DeviceType) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /** When true, overlay a loading indicator over the entry list (used while
   * the dispatch-backed review panel is fetching a newly-selected run's
   * manifest). The header, search, and footer stay interactive. */
  isManifestLoading?: boolean;
  /** Why the active run's manifest could not be loaded, if it couldn't. Shown
   * in place of the (empty) entry list so a failed fetch never reads as a run
   * with zero narratives. */
  manifestError?: string;
  /** A non-blocking caveat about the active run (e.g. it has expired). Shown
   * above the entry list; the list itself still renders. */
  manifestWarning?: string;
  /** Re-run the failed manifest fetch. Rendered as a Retry button when set. */
  onRetryManifest?: () => void;
  /** Workspaces the listed runs come from. The workspace filter row renders
   * only when there is more than one. */
  workspaceOptions?: WorkspaceOption[];
  /** Current workspace filter: a workspace id, or "all". */
  activeWorkspaceId?: string;
  onWorkspaceChange?: (workspaceId: string) => void;
  onEmailExport?: () => void;
}

export function DevNavigator({
  currentReportId,
  manifest,
  allManifests,
  onNavigate,
  onManifestChange,
  onClose,
  deviceType,
  onDeviceTypeChange,
  searchQuery,
  onSearchChange,
  isManifestLoading = false,
  manifestError,
  manifestWarning,
  onRetryManifest,
  workspaceOptions,
  activeWorkspaceId = "all",
  onWorkspaceChange,
  onEmailExport,
}: DevNavigatorProps) {
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? Math.max(MIN_WIDTH, parseInt(saved, 10)) : DEFAULT_WIDTH;
  });
  const isDragging = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  // The switcher entry for the manifest on screen. Match by identity first
  // (App passes the same object it lists). The runId fallback is only for
  // unkeyed local pipeline manifests, where runId is the identity: keyed
  // dispatch entries can share a runId (stubs use the run *name*), so falling
  // back would select a different run during the render between a workspace
  // filter change and App's reconciliation of the active run.
  const activeInfo =
    allManifests.find((m) => m.manifest === manifest) ??
    allManifests.find(
      (m) => m.key == null && m.manifest.runId === manifest.runId,
    );
  const activeKey = activeInfo ? manifestInfoKey(activeInfo) : "";
  const activeLabel = activeInfo
    ? manifestInfoLabel(activeInfo)
    : manifest.runId;

  const navigable = useMemo(
    () =>
      manifest.entries.filter(
        (e) =>
          e.metadata?.report_id &&
          e.file !== "(excluded)" &&
          e.file !== "(error)",
      ),
    [manifest.entries],
  );

  // ── Filter state ──
  const [selectedFlags, setSelectedFlags] = useState<Set<string>>(new Set());

  // ── Sort state ──
  type SortColumn = "status" | "sections" | "pages";
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const toggleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      if (!sortAsc) {
        setSortColumn(null);
        setSortAsc(true);
      } // third click resets
      else setSortAsc(false);
    } else {
      setSortColumn(col);
      setSortAsc(true);
    }
  };

  // Extract flag label (the part before " → ") for grouping
  const flagLabel = (reason: string) => {
    const idx = reason.indexOf(" \u2192 ");
    return idx >= 0 ? reason.slice(0, idx) : reason;
  };

  // Unique flag labels across all navigable entries
  const allFlagLabels = useMemo(() => {
    const set = new Set<string>();
    for (const e of navigable) {
      if (e.flagReasons) e.flagReasons.forEach((r) => set.add(flagLabel(r)));
    }
    return Array.from(set).sort();
  }, [navigable]);

  // Reset filters when manifest changes
  useEffect(() => {
    setSelectedFlags(new Set());
  }, [manifest]);

  const hasActiveFilters = selectedFlags.size > 0;

  const filtered = useMemo(() => {
    let list = navigable;

    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          e.customerName?.toLowerCase().includes(q) ||
          e.customerId?.toLowerCase().includes(q) ||
          e.metadata?.report_id?.toLowerCase().includes(q),
      );
    }

    // Flags filter (AND: entry must match ALL selected flag labels)
    if (selectedFlags.size > 0) {
      list = list.filter(
        (e) =>
          e.flagReasons &&
          [...selectedFlags].every((label) =>
            e.flagReasons!.some((r) => flagLabel(r) === label),
          ),
      );
    }

    // Sort
    if (sortColumn) {
      const statusPriority = (e: (typeof list)[number]) => {
        if (e.defaultsUsed && e.defaultsUsed.length > 0) return 2; // red (missing data)
        if (e.status === "error") return 2;
        if (e.status === "flagged") return 1;
        return 0; // ok (green)
      };
      list = [...list].sort((a, b) => {
        let cmp = 0;
        if (sortColumn === "status")
          cmp = statusPriority(a) - statusPriority(b);
        else if (sortColumn === "sections")
          cmp = (a.sectionCount ?? 0) - (b.sectionCount ?? 0);
        else if (sortColumn === "pages")
          cmp = (a.groupCount ?? 0) - (b.groupCount ?? 0);
        return sortAsc ? cmp : -cmp;
      });
    }

    return list;
  }, [navigable, searchQuery, selectedFlags, sortColumn, sortAsc]);

  // Persist width
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(width));
  }, [width]);

  // Drag-to-resize
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      const startX = e.clientX;
      const startWidth = width;

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        const maxWidth = window.innerWidth * (MAX_WIDTH_PERCENT / 100);
        const newWidth = Math.min(
          maxWidth,
          Math.max(MIN_WIDTH, startWidth + (e.clientX - startX)),
        );
        setWidth(newWidth);
      };

      const onMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  // Escape to close, Cmd/Ctrl+F to focus search
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (searchQuery && document.activeElement === searchRef.current) {
          onSearchChange("");
          searchRef.current?.blur();
        } else {
          onClose();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        const nav = panelRef.current;
        if (nav && nav.offsetParent !== null) {
          e.preventDefault();
          searchRef.current?.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, searchQuery, onSearchChange]);

  const ROW_HEIGHT = 30;

  // Responsive column labels based on panel width
  const isWide = width >= 400;

  // Currently selected entry (from full manifest, not just filtered)
  const selectedEntry = useMemo(
    () =>
      manifest.entries.find((e) => e.metadata?.report_id === currentReportId),
    [manifest.entries, currentReportId],
  );

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  });

  // Arrow key navigation on focused rows
  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const next =
          e.key === "ArrowDown"
            ? Math.min(index + 1, filtered.length - 1)
            : Math.max(index - 1, 0);
        if (next !== index) {
          virtualizer.scrollToIndex(next, { align: "auto" });
          // Focus after scroll settles
          requestAnimationFrame(() => {
            rowRefs.current.get(next)?.focus();
          });
          const reportId = filtered[next].metadata?.report_id;
          if (reportId) onNavigate(reportId);
        }
      }
    },
    [filtered, onNavigate, virtualizer],
  );

  // Scroll to current row on mount
  useEffect(() => {
    const currentIdx = filtered.findIndex(
      (e) => e.metadata?.report_id === currentReportId,
    );
    if (currentIdx >= 0) {
      virtualizer.scrollToIndex(currentIdx, { align: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  return (
    <div
      ref={panelRef}
      data-dev-navigator
      className="hidden xl:flex relative flex-shrink-0 bg-gray-900 border-r border-gray-700 flex-col"
      style={{ width }}
    >
      {/* Header: title row, then the run switcher on its own full-width row so
          long run names stay readable inside a narrow panel. */}
      <div className="px-3 py-2 border-b border-gray-800 bg-gray-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Icon icon="list-ul" className="text-xs text-gray-500" />
            <span className="text-sm font-medium text-white truncate">
              Review Narratives
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 -mr-1"
            title="Close (Esc)"
          >
            <Icon icon="xmark" className="text-sm" />
          </button>
        </div>
        {/* Workspace filter: only meaningful when the viewer can see runs from
            more than one workspace (superusers see all of them). */}
        {workspaceOptions &&
          workspaceOptions.length > 1 &&
          onWorkspaceChange && (
            <div className="flex items-center gap-2 mt-1.5 min-w-0">
              <span className={HEADER_LABEL_CLASS}>Workspace</span>
              <div className="relative flex-1 min-w-0">
                <select
                  value={activeWorkspaceId}
                  onChange={(e) => onWorkspaceChange(e.target.value)}
                  className={HEADER_SELECT_CLASS}
                  aria-label="Filter runs by workspace"
                >
                  <option value="all">All workspaces</option>
                  {workspaceOptions.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.label}
                    </option>
                  ))}
                </select>
                <Icon icon="angle-down" className={HEADER_CHEVRON_CLASS} />
              </div>
            </div>
          )}
        <div className="flex items-center gap-2 mt-1.5 min-w-0">
          <span className={HEADER_LABEL_CLASS}>Run</span>
          {allManifests.length > 1 ? (
            <div className="relative flex-1 min-w-0">
              <select
                value={activeKey}
                onChange={(e) => {
                  const match = allManifests.find(
                    (m) => manifestInfoKey(m) === e.target.value,
                  );
                  if (match) onManifestChange(match);
                }}
                className={HEADER_SELECT_CLASS}
                title={activeLabel}
                aria-label="Switch run"
              >
                {activeKey === "" && (
                  <option value="" disabled hidden>
                    Select a run…
                  </option>
                )}
                {allManifests.map((m) => (
                  <option key={manifestInfoKey(m)} value={manifestInfoKey(m)}>
                    {manifestInfoLabel(m)}
                  </option>
                ))}
              </select>
              <Icon icon="angle-down" className={HEADER_CHEVRON_CLASS} />
            </div>
          ) : (
            <span
              className="text-xs text-gray-300 truncate min-w-0"
              title={activeLabel}
            >
              {activeLabel}
            </span>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-2 py-1.5 border-b border-gray-800 bg-gray-900">
        <div className="relative">
          <Icon
            icon="magnifying-glass"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none"
          />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={`Search ${navigable.length} narratives… (⌘F)`}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-md pl-7 pr-7 py-1 text-xs text-gray-200 placeholder-gray-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => {
                onSearchChange("");
                searchRef.current?.focus();
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors p-0.5"
              title="Clear search"
            >
              <Icon icon="xmark" className="text-xs" />
            </button>
          )}
        </div>
        {/* Narrative count + generated timestamp */}
        <div className="flex items-center justify-between mt-1.5 text-xs text-gray-500">
          <span>
            {searchQuery || hasActiveFilters
              ? `${filtered.length} of ${navigable.length} narrative${navigable.length !== 1 ? "s" : ""}`
              : `${navigable.length} narrative${navigable.length !== 1 ? "s" : ""}`}
          </span>
          {manifest.generatedAt && (
            <span title={new Date(manifest.generatedAt).toISOString()}>
              {new Date(manifest.generatedAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              {new Date(manifest.generatedAt).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>

        {/* Flag filters (inline toggle chips, AND logic) */}
        {allFlagLabels.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            <Icon icon="flag" className="text-[10px] text-gray-600 mr-0.5" />
            {allFlagLabels.map((flag) => {
              const active = selectedFlags.has(flag);
              return (
                <button
                  key={flag}
                  onClick={() =>
                    setSelectedFlags((prev) => {
                      const next = new Set(prev);
                      if (next.has(flag)) next.delete(flag);
                      else next.add(flag);
                      return next;
                    })
                  }
                  className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors leading-tight ${
                    active
                      ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-300"
                      : "bg-gray-800/60 border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600"
                  }`}
                  title={
                    active ? `Remove "${flag}" filter` : `Filter by "${flag}"`
                  }
                >
                  {flag}
                </button>
              );
            })}
            {hasActiveFilters && (
              <button
                onClick={() => setSelectedFlags(new Set())}
                className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors ml-auto"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Manifest fetch failure. Sits above the table rather than replacing it
          so the header and run switcher stay usable for picking another run. */}
      {manifestError && !isManifestLoading && (
        <div
          role="alert"
          className="mx-2 mt-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-2 text-xs text-red-200"
        >
          <Icon
            icon="circle-exclamation"
            className="mt-0.5 flex-shrink-0 text-red-400"
          />
          <div className="min-w-0 flex-1">
            <div className="font-medium">Couldn't load this run</div>
            <div className="mt-0.5 break-words text-red-200/80">
              {manifestError}
            </div>
          </div>
          {onRetryManifest && (
            <button
              onClick={onRetryManifest}
              className="flex-shrink-0 rounded border border-red-400/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-red-200 transition-colors hover:bg-red-500/20"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* Non-blocking caveat (e.g. an expired run). The list still renders. */}
      {manifestWarning && !manifestError && !isManifestLoading && (
        <div
          role="status"
          className="mx-2 mt-2 flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-2 text-xs text-yellow-100"
        >
          <Icon icon="clock" className="mt-0.5 flex-shrink-0 text-yellow-400" />
          <div className="min-w-0 flex-1 break-words text-yellow-100/90">
            {manifestWarning}
          </div>
        </div>
      )}

      {/* Scrollable table (virtualized) */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative">
        {isManifestLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm text-xs text-gray-300">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />
              Loading manifest…
            </div>
          </div>
        )}
        <table className="w-full text-xs text-left">
          <thead className="text-gray-500 uppercase tracking-wider bg-gray-900/80 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-1.5">Customer</th>
              {(
                [
                  ["status", "Status", "S"],
                  ["sections", "Sections", "Sec"],
                  ["pages", "Pages", "Pg"],
                ] as const
              ).map(([col, wide, narrow]) => (
                <th
                  key={col}
                  className="px-2 py-1.5 text-center w-8 cursor-pointer select-none hover:text-gray-300 transition-colors whitespace-nowrap"
                  title={`Sort by ${wide}`}
                  onClick={() => toggleSort(col)}
                >
                  <span className="inline-flex items-center justify-center gap-0.5">
                    {isWide ? wide : narrow}
                    {sortColumn === col && (
                      <span className="text-blue-400 text-[9px] leading-none">
                        {sortAsc ? "▲" : "▼"}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Spacer for rows above the virtual window */}
            {virtualizer.getVirtualItems().length > 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    height: virtualizer.getVirtualItems()[0].start,
                    padding: 0,
                    border: "none",
                  }}
                />
              </tr>
            )}
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const idx = virtualRow.index;
              const entry = filtered[idx];
              const reportId = entry.metadata!.report_id!;
              const isCurrent = reportId === currentReportId;

              return (
                <tr
                  key={entry.customerId}
                  ref={(el) => {
                    if (el) rowRefs.current.set(idx, el);
                    else rowRefs.current.delete(idx);
                  }}
                  data-index={idx}
                  tabIndex={0}
                  onClick={() => onNavigate(reportId)}
                  onKeyDown={(e) => handleRowKeyDown(e, idx)}
                  className={`
                    cursor-pointer outline-none transition-colors
                    hover:bg-gray-800/60
                    ${isCurrent ? "bg-blue-600/30 border-l-2 border-l-blue-400 text-white focus:bg-blue-600/40" : "border-l-2 border-l-transparent focus:bg-gray-800/60"}
                  `}
                  style={{ height: ROW_HEIGHT }}
                >
                  <td className="px-3 py-1.5 text-gray-300 truncate max-w-0">
                    <span className="truncate block">
                      {entry.flagged && (
                        <Icon
                          icon="flag"
                          className="mr-1 text-yellow-500 inline"
                          title={entry.flagReason || "Flagged"}
                        />
                      )}
                      {entry.customerName}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="inline-flex gap-0.5">
                      {entry.status === "flagged" && (
                        <span className="inline-block w-2 h-2 rounded-full bg-yellow-500" />
                      )}
                      {entry.defaultsUsed && entry.defaultsUsed.length > 0 ? (
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                      ) : (
                        entry.status !== "flagged" && (
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${
                              entry.status === "ok"
                                ? "bg-green-500"
                                : entry.status === "error"
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                            }`}
                          />
                        )
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-center text-gray-400">
                    {entry.sectionCount}
                  </td>
                  <td className="px-2 py-1.5 text-center text-gray-400">
                    {entry.groupCount}
                  </td>
                </tr>
              );
            })}
            {/* Spacer for rows below the virtual window */}
            {virtualizer.getVirtualItems().length > 0 &&
              (() => {
                const items = virtualizer.getVirtualItems();
                return (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        height:
                          virtualizer.getTotalSize() -
                          items[items.length - 1].end,
                        padding: 0,
                        border: "none",
                      }}
                    />
                  </tr>
                );
              })()}
          </tbody>
        </table>
      </div>

      {/* Selected entry info panel */}
      {selectedEntry && (
        <div className="border-t border-gray-800 bg-gray-900/60 px-3 py-2 space-y-1.5 max-h-[200px] overflow-y-auto">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Details
            </span>
            <div className="flex items-center gap-1">
              {selectedEntry.status === "flagged" && (
                <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400">
                  flagged
                </span>
              )}
              {selectedEntry.defaultsUsed &&
              selectedEntry.defaultsUsed.length > 0 ? (
                <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">
                  missing data
                </span>
              ) : (
                selectedEntry.status !== "flagged" && (
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                      selectedEntry.status === "ok"
                        ? "bg-green-500/15 text-green-400"
                        : "bg-red-500/15 text-red-400"
                    }`}
                  >
                    {selectedEntry.status}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Section → groups breakdown */}
          {selectedEntry.sectionGroups &&
            Object.keys(selectedEntry.sectionGroups).length > 0 && (
              <div className="space-y-1">
                {Object.entries(selectedEntry.sectionGroups).map(
                  ([section, groups]) => (
                    <div key={section}>
                      <div className="text-xs text-gray-400 font-medium">
                        {section}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {groups.map((g) => (
                          <span
                            key={g}
                            className="text-xs bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded"
                          >
                            {g.replace(`${section}-`, "")}
                          </span>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

          {/* Flag reasons */}
          {selectedEntry.flagReasons &&
            selectedEntry.flagReasons.length > 0 && (
              <div className="space-y-0.5">
                <div className="text-xs text-yellow-500 font-medium flex items-center gap-1">
                  <Icon icon="flag" className="text-xs" />
                  Flags
                </div>
                {selectedEntry.flagReasons.map((reason, i) => (
                  <div key={i} className="text-xs text-yellow-400/80 pl-3">
                    {reason}
                  </div>
                ))}
              </div>
            )}

          {/* Defaults used */}
          {selectedEntry.defaultsUsed &&
            selectedEntry.defaultsUsed.length > 0 && (
              <div>
                <div className="text-xs text-red-500 font-medium">
                  Defaults used (missing data)
                </div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {selectedEntry.defaultsUsed.map((d) => (
                    <span
                      key={d}
                      className="text-xs bg-red-500/10 text-red-400/80 px-1.5 py-0.5 rounded"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Admin emails */}
          {selectedEntry.metadata?.admin_emails && (
            <div>
              <div className="text-xs text-gray-500 font-medium">
                Admin emails
              </div>
              <div className="text-xs text-gray-400 break-all leading-relaxed">
                {(selectedEntry.metadata.admin_emails as string)
                  .split("|")
                  .join(", ")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-800 px-3 py-2">
        {/* Device selector */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-800/80 rounded-md p-0.5 border border-gray-700">
            {deviceTypes.map((type) => (
              <button
                key={type}
                onClick={() => onDeviceTypeChange(type)}
                className={`
                  flex items-center gap-1.5 px-2 py-1 rounded transition-all duration-200
                  ${
                    deviceType === type
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-gray-400 hover:text-white hover:bg-gray-700/50"
                  }
                `}
                title={deviceConfig[type].label}
              >
                <Icon icon={deviceConfig[type].icon} className="text-xs" />
                <span className="text-xs font-medium">
                  {deviceConfig[type].label}
                </span>
              </button>
            ))}
          </div>
          {onEmailExport && (
            <button
              onClick={onEmailExport}
              className="flex items-center gap-1.5 px-2 py-1 bg-gray-800/80 rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
              title="Email Export"
            >
              <Icon icon="envelope" className="text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* Resize handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500/50 active:bg-blue-500/70 transition-colors"
        onMouseDown={handleMouseDown}
      />
    </div>
  );
}

/**
 * Toggle button shown when the DevNavigator panel is closed.
 * Rendered outside the panel so it's always visible.
 */
export function DevNavigatorToggle({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="hidden xl:flex fixed top-3 left-3 z-[200] items-center gap-1.5 px-2 py-1 bg-gray-800/80 backdrop-blur-sm rounded-md border border-gray-700 text-gray-400 hover:text-white transition-all duration-200"
      title="Dev Navigator"
    >
      <Icon icon="list-ul" className="text-xs" />
      <span className="text-xs font-medium">
        {count} narrative{count !== 1 ? "s" : ""}
      </span>
    </button>
  );
}
